import React, {useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import _ from 'lodash';

import DocumentHeader from '../../components/DocumentHeader';
import CourseLessonsProvider from '../../components/CourseLessonsProvider';
import BlogPostCard from '../../components/BlogPostCard';
import ResourceCard from '../../components/ResourceCard';
import {useAnalytics, useAppContext} from '../../hooks';
import CourseLibrary from './CourseLibrary';
import {createURLWithParams} from '../../util';
import {getCanonicalURL} from '../../util/seo';
import {getSearchResults, getTags} from '../../api/api';

const ARRAY_FORMAT = 'none';

const CoursesPage = () => {
  const {loading} = useAppContext();
  const {tracker} = useAnalytics();
  const router = useRouter();
  const {tag, query} = router.query;
  const [relatedContent, setRelatedContent] = useState({
    blog: [],
    resources: [],
  });
  const [tagMap, setTagMap] = useState({});

  useEffect(() => {
    if (!loading) tracker.courseListView();
  }, [loading]);

  useEffect(() => {
    const getAndSetTags = async () => {
      const tags = await getTags();
      const tagMapById = _.mapValues(_.keyBy(tags, 'id'), 'name');
      setTagMap(tagMapById);
    };
    getAndSetTags();
  }, []);

  useEffect(() => {
    // Get search results for related blog and resource content
    const getRelatedContent = async () => {
      const searchResults = await getSearchResults({query: query || tagMap[tag] || tag});
      setRelatedContent({
        blog: searchResults.blogPosts,
        resources: searchResults.resources,
      });
    };

    if (query || tag) {
      getRelatedContent();
    }
  }, [query, tag, tagMap]);

  const updateParams = (obj, isPush = true) => {
    const url = createURLWithParams('/courses', {tag, query, ...obj}, {arrayFormat: ARRAY_FORMAT});
    if (isPush) {
      router.push(url, null, {scroll: false});
    } else {
      router.replace(url, null, {scroll: false});
    }
  };

  const tagName = tagMap[tag];

  return (
    <div className="min-h-screen">
      <DocumentHeader
        title={tagName ? `${tagName} Courses` : 'Course Library'}
        canonicalUrl={tagName ? getCanonicalURL(router.asPath, ['tag']) : undefined}
      />
      {/* White background for the header area */}
      <div className="bg-white border-b border-gray-100">
        <CourseLessonsProvider renderWhileLoading>
          <div className="mx-auto max-w-7xl pb-16 pt-8 px-4 lg:px-8">
            <CourseLibrary tag={tag} query={query} updateParams={updateParams} tagMap={tagMap} />
          </div>
        </CourseLessonsProvider>
      </div>
      {/* Gray background for related content */}
      <div className="bg-gray-50">
        {relatedContent.blog.length > 0 && (
          <div className="mx-auto max-w-7xl pb-12 pt-8 px-4 lg:px-8">
            <h2 className="mb-4 text-lg lg:text-xl font-bold">{tagName ? `Related ${tagName} Articles` : 'Related Articles'}</h2>
            <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedContent.blog.map(({item}) => (
                <BlogPostCard post={item} key={item.slug} />
              ))}
            </div>
          </div>
        )}
        {relatedContent.resources.length > 0 && (
          <div className="mx-auto max-w-7xl pb-12 px-4 lg:px-8">
            <h2 className="mb-4 text-lg lg:text-xl font-bold">{tagName ? `Related ${tagName} Resources` : 'Related Resources'}</h2>
            <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedContent.resources.map(({item}) => (
                <ResourceCard post={item} badge={item.free ? 'Free' : null} key={item.slug} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesPage;
